import { Icon } from "@chakra-ui/react";
import { MdBarChart } from "react-icons/md";
import MiniStatistics from "../../../components/card/MiniStatistics";
import IconBox from "../../../components/icons/IconBox";
import { translate } from "../../../i18n";
import { useSelectedOrganization } from "../../organization/hooks/useSelectedOrganization";
import { getDemoGrowth, getDemoUserCount, isDemoOrganization } from "../demoAnalyticsData";
import { useGetUserCount } from "../hooks/useGetUserCount";
import { computeGrowth } from "../utils/computeGrowth";
import { UNKNOWN_STAT_VALUE, isUnreadable, statPlaceholder } from "../utils/statValue";

type Props = {
    boxColor: string;
    brandColor: string;
    startDate: Date;
    endDate: Date;
    previousStartDate: Date | null;
    previousEndDate: Date | null;
    growthLabel?: string;
};
export default function UserCountCard({ boxColor, brandColor, startDate, endDate, previousStartDate, previousEndDate, growthLabel }: Props) {
    const { selectedOrganization } = useSelectedOrganization();
    const isDemo = isDemoOrganization(selectedOrganization?.id);
    /**
     * Every `analytics/*` route carries `@UseGuards(AdminAuthGuard)` — read from
     * `api/src/analytics/analytics.controller.ts` this run, not inherited — so a
     * stale dashboard token or an operator who is no longer an admin of this org
     * is REFUSED, with no outage at all, on top of the ordinary 5xx/network
     * paths. All of them leave `data === undefined`, which this card used to
     * render as a real number.
     */
    const { data, isInitialLoading: isPending, isError } = useGetUserCount(selectedOrganization?.id);
    // Comparison reads: they feed `computeGrowth` only, which returns undefined
    // when either side is undefined, so a failed read removes the growth chip
    // rather than mis-stating it. Allowlisted in `analytics-read-error-gate`.
    const { data: currentData } = useGetUserCount(
        selectedOrganization?.id,
        startDate,
        endDate,
    );
    const { data: previousData } = useGetUserCount(
        selectedOrganization?.id,
        previousStartDate ?? undefined,
        previousEndDate ?? undefined,
    );

    const orgId = selectedOrganization?.id;
    const unreadable = isUnreadable(isError, data);
    const demoValue = isDemo ? getDemoUserCount(startDate, endDate, orgId) : 0;
    const growth = isDemo
        ? getDemoGrowth(demoValue, previousStartDate, previousEndDate, getDemoUserCount, orgId)
        : previousStartDate ? computeGrowth(currentData?.value, previousData?.value) : undefined;
    /**
     * `Boolean(data)`, not `Boolean(data?.value)`, and a placeholder rather than
     * a literal `'0'` in the last arm.
     *
     * This read is gated on `!!organizationId`, and the selected organization is
     * `undefined` for a whole session whenever `my-organizations` is refused or
     * answers `[]` — the admin layout renders once that read RESOLVES, and an
     * error counts as resolved (`useMyOrganizationsResolved`), while
     * `placeholderData: []` stops the auto-select from ever firing. The query
     * therefore sits DISABLED, React Query v4 reports `isLoading: true` with
     * `fetchStatus: 'idle'`, and this card showed `--` with no end (R100-1).
     * With the last arm no longer a zero, testing the VALUE for truthiness would
     * send an organization that genuinely has no users to it — so test the
     * response: `{ value: 0 }` is an answer.
     */
    const value = isDemo ? demoValue : unreadable ? UNKNOWN_STAT_VALUE : Boolean(data) ? data?.value : statPlaceholder(isPending);

    return (
        <MiniStatistics
            startContent={
                <IconBox
                    w='56px'
                    h='56px'
                    bg={boxColor}
                    icon={<Icon w='32px' h='32px' as={MdBarChart} color={brandColor} />}
                />
            }
            name={translate('tabs.users')}
            value={value}
            isError={!isDemo && unreadable}
            growth={growth}
            growthLabel={growthLabel}
        />)
}
